// src/pages/admin/AllAgentsKycPage.jsx
import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Modal,
  Typography,
  Divider,
  Tabs,
  Statistic,
  Tooltip,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SafetyOutlined,
  UserOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  PhoneOutlined,
  HomeOutlined,
  StarOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileUnknownOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../api/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import notify from "../../utils/notify";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

// ─── helpers ────────────────────────────────────────────────────────────────

const KYC_STATUS_FILTER = [
  { value: "all", label: "All KYC Statuses" },
  { value: "notstarted", label: "Not Started" },
  { value: "submitted", label: "Submitted" },
  { value: "review", label: "Under Review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "resubmission", label: "Resubmission Required" },
];

const VERIFICATION_FILTER = [
  { value: "all", label: "All Agents" },
  { value: "verified", label: "Verified Only" },
  { value: "unverified", label: "Unverified Only" },
];

const kycTagColor = (s) => {
  const v = String(s ?? "").toLowerCase();
  if (v.includes("approved") || v === "4") return "success";
  if (v.includes("rejected") || v === "5") return "error";
  if (v.includes("resubmission") || v === "6") return "warning";
  if (v.includes("review") || v === "3" || v.includes("submitted") || v === "2") return "processing";
  return "default";
};

const kycLabel = (s) => {
  const v = String(s ?? "").toLowerCase();
  if (v.includes("approved") || v === "4") return "Approved";
  if (v.includes("rejected") || v === "5") return "Rejected";
  if (v.includes("resubmission") || v === "6") return "Resubmission Required";
  if (v.includes("review") || v === "3") return "Under Review";
  if (v.includes("submitted") || v === "2") return "Submitted";
  if (v === "notstarted" || v === "1") return "Not Started";
  return String(s);
};

const fmt = (utc) => (utc ? new Date(utc).toLocaleString() : "—");
const fmtDate = (utc) => (utc ? new Date(utc).toLocaleDateString() : "—");

const getKycStep = (status) => {
  const v = String(status ?? "").toLowerCase();
  if (v.includes("notstarted") || v === "1") return 0;
  if (v.includes("submitted") || v === "2") return 1;
  if (v.includes("review") || v === "3") return 2;
  if (v.includes("approved") || v === "4") return 3;
  if (v.includes("rejected") || v === "5") return 3;
  if (v.includes("resubmission") || v === "6") return 1;
  return 0;
};

// Get file icon based on extension
const getFileIcon = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 24 }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return <FileImageOutlined style={{ color: '#52c41a', fontSize: 24 }} />;
  return <FileTextOutlined style={{ color: 'var(--hms-accent)', fontSize: 24 }} />;
};

// Check if file is an image
const isImageFile = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
};

// Check if file is PDF
const isPdfFile = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  return ext === 'pdf';
};

// Revoke object URL helper
const revokeObjectUrl = (url) => {
  if (url) {
    window.URL.revokeObjectURL(url);
  }
};

// Extract error message from blob response
const extractBlobErrorMessage = async (error, fallbackMessage) => {
  try {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          return parsed?.message || fallbackMessage;
        } catch {
          return text || fallbackMessage;
        }
      }
    }
    return error?.response?.data?.message || error?.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

// Get admin document URL
const getAdminDocumentUrl = (agentProfileId, documentId) => {
  return `/agents/admin/${agentProfileId}/documents/${documentId}`;
};

// ─── Main Component ──────────────────────────────────────────────────────────

const AllAgentsKycPage = () => {
  const { authData } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = authData?.role === "SystemAdmin" || authData?.isSuperAdmin;

  const [kycFilter, setKycFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [activeTab, setActiveTab] = useState("kyc");
  
  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedAgent(null);
    setReviewNotes("");
  };

  const openDocumentViewer = (doc) => {
    setSelectedDocument(doc);
    setViewerOpen(true);
  };

  const closeDocumentViewer = () => {
    setViewerOpen(false);
    setSelectedDocument(null);
    setDocumentUrl((current) => {
      revokeObjectUrl(current);
      return null;
    });
  };

  // Load document for viewing
  React.useEffect(() => {
    if (!viewerOpen || !selectedDocument?.agentKycDocumentId || !selectedAgent?.agentProfileId) return;

    let cancelled = false;
    let createdUrl = null;

    const loadDocument = async () => {
      setLoadingDocument(true);
      setDocumentUrl((current) => {
        revokeObjectUrl(current);
        return null;
      });

      try {
        const response = await axiosInstance.get(
          getAdminDocumentUrl(selectedAgent.agentProfileId, selectedDocument.agentKycDocumentId),
          { responseType: "blob" }
        );
        if (cancelled) return;
        createdUrl = window.URL.createObjectURL(response.data);
        setDocumentUrl(createdUrl);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load document:", error);
        const msg = await extractBlobErrorMessage(error, "Failed to load document.");
        notify.error(msg);
      } finally {
        if (!cancelled) {
          setLoadingDocument(false);
        }
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
      revokeObjectUrl(createdUrl);
    };
  }, [viewerOpen, selectedDocument, selectedAgent]);

  // ── queries ──────────────────────────────────────────────────────────────

  const agentsQuery = useQuery({
    queryKey: ["all-agents-kyc"],
    enabled: true,
    retry: false,
    queryFn: async () => {
      try {
        const res = await axiosInstance.get(`/agents/admin/all`);
        console.log("Agents data:", res.data);
        return Array.isArray(res.data) ? res.data : [];
      } catch (error) {
        console.error("Error fetching agents:", error);
        notify.error("Failed to load agents data");
        return [];
      }
    },
  });

  const agentDetailQuery = useQuery({
    queryKey: ["agent-kyc-detail", selectedAgent?.agentProfileId],
    enabled: !!selectedAgent && drawerOpen,
    retry: false,
    queryFn: async () => {
      try {
        const res = await axiosInstance.get(
          `/agents/admin/${selectedAgent.agentProfileId}/kyc`
        );
        console.log("Agent detail response:", res.data);
        return res.data;
      } catch (error) {
        console.error("Error fetching agent detail:", error);
        notify.error("Failed to load agent KYC details");
        return null;
      }
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["all-agents-kyc"] });
    if (selectedAgent) {
      queryClient.invalidateQueries({
        queryKey: ["agent-kyc-detail", selectedAgent.agentProfileId],
      });
    }
  };

  // ── mutations ─────────────────────────────────────────────────────────────

  const approveKycMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(
        `/agents/admin/${selectedAgent.agentProfileId}/kyc/approve`,
        { reviewNotes }
      );
      return res.data;
    },
    onSuccess: () => {
      notify.success("KYC approved successfully.");
      closeDrawer();
      refresh();
    },
    onError: (e) => notify.error(e?.response?.data?.message || "Approval failed."),
  });

  const rejectKycMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(
        `/agents/admin/${selectedAgent.agentProfileId}/kyc/reject`,
        { reviewNotes }
      );
      return res.data;
    },
    onSuccess: () => {
      notify.success("KYC rejected.");
      closeDrawer();
      refresh();
    },
    onError: (e) => notify.error(e?.response?.data?.message || "Reject failed."),
  });

  const resubmitMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(
        `/agents/admin/${selectedAgent.agentProfileId}/kyc/request-resubmission`,
        { reviewNotes }
      );
      return res.data;
    },
    onSuccess: () => {
      notify.success("Resubmission requested.");
      closeDrawer();
      refresh();
    },
    onError: (e) => notify.error(e?.response?.data?.message || "Request failed."),
  });

  const verifyAgentMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(
        `/agents/admin/${selectedAgent.agentProfileId}/verify`,
        { verified: true }
      );
      return res.data;
    },
    onSuccess: () => {
      notify.success("Agent verified successfully.");
      refresh();
    },
    onError: (e) => notify.error(e?.response?.data?.message || "Verification failed."),
  });

  // ── derived state ─────────────────────────────────────────────────────────

  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);

  const filtered = useMemo(() => {
    let result = agents;

    if (kycFilter !== "all") {
      result = result.filter((a) => {
        const kyc = String(a.latestKycStatus ?? a.kycStatus ?? "notstarted").toLowerCase();
        return kyc.includes(kycFilter);
      });
    }

    if (verificationFilter === "verified") {
      result = result.filter((a) => a.isVerified);
    } else if (verificationFilter === "unverified") {
      result = result.filter((a) => !a.isVerified);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (a) =>
          a.displayName?.toLowerCase().includes(term) ||
          a.email?.toLowerCase().includes(term) ||
          a.companyName?.toLowerCase().includes(term) ||
          a.firstName?.toLowerCase().includes(term) ||
          a.lastName?.toLowerCase().includes(term)
      );
    }

    return result;
  }, [agents, kycFilter, verificationFilter, searchTerm]);

  const kycCounts = useMemo(() => {
    const counts = {
      submitted: 0,
      review: 0,
      approved: 0,
      rejected: 0,
      resubmission: 0,
      notstarted: 0,
    };
    agents.forEach((a) => {
      const k = String(a.latestKycStatus ?? a.kycStatus ?? "notstarted").toLowerCase();
      if (k.includes("approved") || k === "4") counts.approved++;
      else if (k.includes("rejected") || k === "5") counts.rejected++;
      else if (k.includes("resubmission") || k === "6") counts.resubmission++;
      else if (k.includes("review") || k === "3") counts.review++;
      else if (k.includes("submitted") || k === "2") counts.submitted++;
      else counts.notstarted++;
    });
    return counts;
  }, [agents]);

  const openDrawer = (agent) => {
    setSelectedAgent(agent);
    setReviewNotes("");
    setDrawerOpen(true);
  };

  // Helper to safely get KYC data from response
  const getKycData = () => {
    const data = agentDetailQuery.data;
    if (!data) return null;
    return data.kyc || data.KYC || data;
  };

  const getDocuments = () => {
    const data = agentDetailQuery.data;
    if (!data) return [];
    return data.documents || data.Documents || data.kyc?.documents || [];
  };

  const getApplications = () => {
    const data = agentDetailQuery.data;
    if (!data) return [];
    return data.applications || data.Applications || [];
  };

  const kycData = getKycData();
  const documents = getDocuments();
  const applications = getApplications();

  // ─── columns ───────────────────────────────────────────────────────────────

  const columns = [
    {
      title: "Agent",
      key: "agent",
      width: 280,
      render: (_, row) => (
        <Space>
          <Avatar icon={<UserOutlined />} style={{ background: "var(--hms-accent)" }} />
          <div>
            <div style={{ fontWeight: 600 }}>
              {row.displayName || `${row.firstName} ${row.lastName}`}
              {row.isVerified && (
                <CheckCircleOutlined style={{ color: "#52c41a", marginLeft: 6 }} />
              )}
            </div>
            <div style={{ color: "#8c8c8c", fontSize: 12 }}>{row.email}</div>
            {row.companyName && (
              <div style={{ color: "#8c8c8c", fontSize: 11 }}>{row.companyName}</div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      width: 150,
      render: (_, row) => (
        <div>
          {row.phoneNumber && (
            <div style={{ fontSize: 12 }}>
              <PhoneOutlined style={{ marginRight: 6 }} />
              {row.phoneNumber}
            </div>
          )}
          {row.location && (
            <div style={{ fontSize: 12, marginTop: 4 }}>
              <HomeOutlined style={{ marginRight: 6 }} />
              {row.location}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "KYC Status",
      key: "kyc",
      width: 140,
      render: (_, row) => {
        const kyc = row.latestKycStatus ?? row.kycStatus ?? "NotStarted";
        return (
          <Tag color={kycTagColor(kyc)} style={{ fontSize: 12 }}>
            {kycLabel(kyc)}
          </Tag>
        );
      },
    },
    {
      title: "Submitted",
      key: "submitted",
      width: 120,
      render: (_, row) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {row.latestKycSubmittedAt ? fmtDate(row.latestKycSubmittedAt) : "—"}
        </Text>
      ),
    },
    {
      title: "Hotels",
      key: "hotels",
      width: 100,
      render: (_, row) => (
        <Badge
          count={row.activeHotelsCount || 0}
          showZero
          style={{ backgroundColor: "var(--hms-accent)" }}
        />
      ),
    },
    {
      title: "Rating",
      key: "rating",
      width: 100,
      render: (_, row) => (
        <div>
          <StarOutlined style={{ color: "#faad14", marginRight: 4 }} />
          <span>{row.averageRating?.toFixed(1) || "N/A"}</span>
          <span style={{ fontSize: 11, color: "#8c8c8c", marginLeft: 4 }}>
            ({row.totalReviews || 0})
          </span>
        </div>
      ),
    },
    {
      title: "Joined",
      key: "joined",
      width: 100,
      render: (_, row) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {fmtDate(row.createdAtUtc)}
        </Text>
      ),
    },
    {
      title: "",
      key: "action",
      width: 100,
      fixed: "right",
      render: (_, row) => (
        <Space>
          <Tooltip title="View KYC">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openDrawer(row)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── render ────────────────────────────────────────────────────────────────

  if (!isAdmin) {
    return (
      <Alert
        type="error"
        message="Access Denied"
        description="You don't have permission to view this page."
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Space direction="vertical" size={20} style={{ width: "100%" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <Title level={3} style={{ marginBottom: 4 }}>Agent KYC Management</Title>
            <Text type="secondary">
              Review and manage KYC submissions from all registered travel agents.
            </Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={refresh}>
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} lg={4}>
            <Card style={{ borderRadius: 10 }}>
              <Statistic
                title="Total Agents"
                value={agents.length}
                prefix={<UserOutlined />}
                styles={{ value: { color: "var(--hms-accent)" } }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card style={{ borderRadius: 10, background: "var(--hms-wash)", borderColor: "var(--hms-accent)" }}>
              <Statistic
                title="Pending Review"
                value={kycCounts.submitted + kycCounts.review}
                prefix={<ClockCircleOutlined />}
                styles={{ value: { color: "var(--hms-accent)" } }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card style={{ borderRadius: 10, background: "#f6ffed", borderColor: "#b7eb8f" }}>
              <Statistic
                title="Approved"
                value={kycCounts.approved}
                prefix={<CheckCircleOutlined />}
                styles={{ value: { color: "#52c41a" } }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card style={{ borderRadius: 10, background: "#fff2f0", borderColor: "#ffccc7" }}>
              <Statistic
                title="Rejected"
                value={kycCounts.rejected}
                prefix={<CloseCircleOutlined />}
                styles={{ value: { color: "#ff4d4f" } }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card style={{ borderRadius: 10, background: "#fff7e6", borderColor: "#ffd591" }}>
              <Statistic
                title="Needs Resubmit"
                value={kycCounts.resubmission}
                prefix={<WarningOutlined />}
                styles={{ value: { color: "#fa8c16" } }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card style={{ borderRadius: 10 }}>
              <Statistic
                title="Verified Agents"
                value={agents.filter(a => a.isVerified).length}
                prefix={<CheckCircleOutlined />}
                styles={{ value: { color: "#52c41a" } }}
              />
            </Card>
          </Col>
        </Row>

        {/* Pending Alert */}
        {kycCounts.submitted + kycCounts.review > 0 && (
          <Alert
            type="info"
            showIcon
            icon={<ClockCircleOutlined />}
            message={
              <Text>
                <Text strong>{kycCounts.submitted + kycCounts.review}</Text> agent
                {kycCounts.submitted + kycCounts.review !== 1 ? "s" : ""} have KYC documents awaiting your review.
              </Text>
            }
            action={
              <Button size="small" onClick={() => setKycFilter("submitted")}>
                Review Now
              </Button>
            }
          />
        )}

        {/* Filters */}
        <Card style={{ borderRadius: 10 }}>
          <div style={{ padding: "16px" }}>
            <Row gutter={[16, 12]} align="middle">
              <Col xs={24} sm={8}>
                <Input
                  placeholder="Search by name, email, or company..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={12} sm={6}>
                <Select
                  style={{ width: "100%" }}
                  value={kycFilter}
                  onChange={setKycFilter}
                  options={KYC_STATUS_FILTER}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Select
                  style={{ width: "100%" }}
                  value={verificationFilter}
                  onChange={setVerificationFilter}
                  options={VERIFICATION_FILTER}
                />
              </Col>
              <Col xs={24} sm={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Showing {filtered.length} of {agents.length}
                </Text>
              </Col>
            </Row>
          </div>
        </Card>

        {/* Agents Table */}
        <Card style={{ borderRadius: 10 }}>
          <Table
            rowKey="agentProfileId"
            columns={columns}
            dataSource={filtered}
            loading={agentsQuery.isLoading}
            pagination={{ pageSize: 15, showSizeChanger: true }}
            scroll={{ x: 1000 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No agents found."
                />
              ),
            }}
          />
        </Card>
      </Space>

      {/* ── KYC Detail Drawer ── */}
      <Drawer
        title={
          <Space>
            <SafetyOutlined />
            Agent KYC Review
            {selectedAgent && (
              <Tag color={selectedAgent.isVerified ? "success" : "default"}>
                {selectedAgent.isVerified ? "Verified" : "Unverified"}
              </Tag>
            )}
          </Space>
        }
        size="large"
        open={drawerOpen}
        onClose={closeDrawer}
        extra={
          <Space>
            {selectedAgent && !selectedAgent.isVerified && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => verifyAgentMutation.mutate()}
                loading={verifyAgentMutation.isPending}
              >
                Verify Agent
              </Button>
            )}
          </Space>
        }
      >
        {agentDetailQuery.isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Spin />
          </div>
        ) : agentDetailQuery.data ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {/* Agent Info */}
            <Card size="small" style={{ borderRadius: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <Avatar size={56} icon={<UserOutlined />} style={{ background: "var(--hms-accent)" }} />
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    {selectedAgent?.displayName || `${selectedAgent?.firstName} ${selectedAgent?.lastName}`}
                  </Title>
                  <Text type="secondary">{selectedAgent?.email}</Text>
                  <br />
                  {selectedAgent?.companyName && (
                    <Text type="secondary">{selectedAgent?.companyName}</Text>
                  )}
                </div>
              </div>
              <Divider style={{ margin: "12px 0" }} />
              <Row gutter={16}>
                <Col span={12}>
                  <Text type="secondary">Phone</Text>
                  <div>{selectedAgent?.phoneNumber || "—"}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Location</Text>
                  <div>{selectedAgent?.location || "—"}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">License Number</Text>
                  <div>{selectedAgent?.licenseNumber || "—"}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Years Experience</Text>
                  <div>{selectedAgent?.yearsExperience || "—"}</div>
                </Col>
              </Row>
            </Card>

            {/* KYC Progress */}
            <Card size="small" title="KYC Progress" style={{ borderRadius: 10 }}>
              <Steps
                size="small"
                current={getKycStep(kycData?.reviewStatus)}
                status={
                  kycData?.reviewStatus?.toLowerCase().includes("rejected")
                    ? "error"
                    : kycData?.reviewStatus?.toLowerCase().includes("approved")
                    ? "finish"
                    : "process"
                }
                items={[
                  { title: "Not Started" },
                  { title: "Submitted" },
                  { title: "Under Review" },
                  { title: "Approved" },
                ]}
              />
            </Card>

            {/* KYC Submissions */}
            <Card
              size="small"
              title="KYC Submissions"
              style={{ borderRadius: 10 }}
            >
              <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab="Identity Details" key="kyc">
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Identity Number">
                      {kycData?.identityNumber || "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Business Registration">
                      {kycData?.businessRegistrationNumber || "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tax Number">
                      {kycData?.taxNumber || "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Submitted Date">
                      {fmtDate(kycData?.submittedAtUtc)}
                    </Descriptions.Item>
                    {kycData?.reviewedAtUtc && (
                      <Descriptions.Item label="Last Reviewed">
                        {fmt(kycData?.reviewedAtUtc)}
                      </Descriptions.Item>
                    )}
                    {kycData?.reviewNotes && (
                      <Descriptions.Item label="Review Notes">
                        {kycData?.reviewNotes}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </TabPane>

                <TabPane tab="Documents" key="documents">
                  {documents.length > 0 ? (
                    <Row gutter={[12, 12]}>
                      {documents.map((doc) => (
                        <Col xs={24} sm={12} key={doc.agentKycDocumentId}>
                          <Card
                            size="small"
                            hoverable
                            style={{ borderRadius: 8 }}
                            styles={{ body: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 } }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              {getFileIcon(doc.fileName)}
                              <div>
                                <div style={{ fontWeight: 600 }}>{doc.documentType}</div>
                                <div style={{ color: "#8c8c8c", fontSize: 12 }}>{doc.fileName}</div>
                                <div style={{ color: "#8c8c8c", fontSize: 11 }}>
                                  Uploaded: {fmtDate(doc.uploadedAtUtc)}
                                </div>
                              </div>
                            </div>
                            <Button
                              type="link"
                              icon={<EyeOutlined />}
                              onClick={() => openDocumentViewer(doc)}
                              style={{ flexShrink: 0 }}
                            >
                              View
                            </Button>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <Empty description="No documents uploaded" />
                  )}
                </TabPane>

                <TabPane tab="Applications" key="applications">
                  {applications.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {applications.map((app) => (
                        <Card key={app.hotelAgentApplicationId} size="small">
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                              <Text strong>{app.hotelName}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Applied: {fmtDate(app.appliedAtUtc)}
                              </Text>
                            </div>
                            <Tag color={app.status === "Approved" ? "success" : "warning"}>
                              {app.status}
                            </Tag>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Empty description="No applications yet" />
                  )}
                </TabPane>
              </Tabs>
            </Card>

            {/* Review Actions */}
            {kycData?.reviewStatus?.toLowerCase() !== "approved" && (
              <Card
                size="small"
                title="KYC Decision"
                style={{
                  borderRadius: 10,
                  background: "var(--hms-wash)",
                  border: "1px solid var(--hms-accent)",
                }}
              >
                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                  <div>
                    <Text strong>Review Notes</Text>
                    <TextArea
                      rows={3}
                      style={{ marginTop: 6 }}
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add notes for the agent about your decision..."
                    />
                  </div>
                  <Divider style={{ margin: "4px 0" }} />
                  <Row gutter={[8, 8]}>
                    <Col xs={24} sm={8}>
                      <Button
                        type="primary"
                        block
                        icon={<CheckCircleOutlined />}
                        loading={approveKycMutation.isPending}
                        onClick={() => approveKycMutation.mutate()}
                      >
                        Approve KYC
                      </Button>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Button
                        danger
                        block
                        icon={<CloseCircleOutlined />}
                        loading={rejectKycMutation.isPending}
                        onClick={() => rejectKycMutation.mutate()}
                      >
                        Reject KYC
                      </Button>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Button
                        block
                        icon={<WarningOutlined />}
                        loading={resubmitMutation.isPending}
                        onClick={() => resubmitMutation.mutate()}
                      >
                        Request Resubmit
                      </Button>
                    </Col>
                  </Row>
                </Space>
              </Card>
            )}
          </Space>
        ) : (
          <Empty description="No KYC data found for this agent." />
        )}
      </Drawer>

      {/* ── Document Viewer Modal ── */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            {selectedDocument?.documentType || "Document Viewer"}
          </Space>
        }
        open={viewerOpen}
        onCancel={closeDocumentViewer}
        footer={[
          <Button key="close" onClick={closeDocumentViewer}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            disabled={!selectedDocument?.agentKycDocumentId || !selectedAgent?.agentProfileId}
            onClick={async () => {
              if (!selectedDocument?.agentKycDocumentId || !selectedAgent?.agentProfileId) return;
              try {
                const response = await axiosInstance.get(
                  getAdminDocumentUrl(selectedAgent.agentProfileId, selectedDocument.agentKycDocumentId),
                  { responseType: "blob" }
                );
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = selectedDocument.fileName || "document";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                notify.success("Download started");
              } catch (error) {
                console.error("Download error:", error);
                const msg = await extractBlobErrorMessage(error, "Failed to download file.");
                notify.error(msg);
              }
            }}
          >
            Download
          </Button>,
        ]}
        width="80%"
        style={{ top: 20 }}
        styles={{ body: { padding: "20px", minHeight: "60vh", maxHeight: "70vh", overflow: "auto" } }}
      >
        {loadingDocument ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Loading document...</div>
          </div>
        ) : selectedDocument && documentUrl ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
            {isImageFile(selectedDocument.fileName) ? (
              <img
                src={documentUrl}
                alt={selectedDocument.fileName}
                style={{ maxWidth: "100%", maxHeight: "60vh", objectFit: "contain" }}
              />
            ) : isPdfFile(selectedDocument.fileName) ? (
              <iframe
                src={documentUrl}
                title={selectedDocument.fileName}
                style={{ width: "100%", height: "60vh", border: "none" }}
              />
            ) : (
              <div style={{ textAlign: "center" }}>
                <FileUnknownOutlined style={{ fontSize: 64, color: "#8c8c8c" }} />
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">Preview not available for this file type.</Text>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Text type="secondary">Could not load document.</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AllAgentsKycPage;